import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";

const IMG = {
  cargo: "https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4?auto=format&fit=crop&w=1300&q=80",
  containers: "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?auto=format&fit=crop&w=1200&q=80",
};

const process = [
  { n: "01", title: "Understand your goals", body: "We start by learning your objectives — the markets you want to enter, the problems you're solving, and where international complexity is slowing you down." },
  { n: "02", title: "Advise & strategize", body: "We provide consulting on the intricacies of foreign markets, local regulations, and business norms — building a clear strategy to expand and compete abroad." },
  { n: "03", title: "Source & purchase", body: "When you need parts or components, we identify, vet, and connect you with reliable overseas suppliers and coordinate purchasing on your behalf." },
  { n: "04", title: "Coordinate logistics & delivery", body: "We coordinate freight, customs, and shipping to get goods delivered on a predictable schedule, with a single point of contact throughout." },
];

const values = [
  { title: "Reliability", body: "Predictable communication and dependable execution — the qualities business and technical buyers actually need." },
  { title: "Transparency", body: "Clear pricing, clear timelines, and honest guidance on trade-offs at every step." },
  { title: "Clarity", body: "We absorb the complexity of operating internationally so your team stays focused on growing the business." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10 pt-40 pb-20 md:pt-52 md:pb-28">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <p className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-primary">[ About Prescience Service ]</p>
          <h1 className="font-heading text-[12vw] font-black uppercase leading-[0.85] tracking-tighter md:text-[8vw]">
            <MaskedLines lines={["Clarity for", "global business"]} />
          </h1>
          <Reveal delay={0.4} className="mt-10 max-w-2xl">
            <p className="font-mono text-sm leading-relaxed text-muted-foreground md:text-base">
              Prescience Service exists to make international operations feel simple. First and foremost,
              we are an international consulting partner — helping small to medium sized companies understand
              foreign markets and expand with confidence. When the work calls for it, we also source, purchase,
              and ship components from outside your home country. Consulting comes first; sourcing follows.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MISSION + IMAGE */}
      <section className="relative z-10 mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <Reveal className="md:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary">[ Our mission ]</p>
            <h2 className="mt-6 font-heading text-3xl font-bold leading-tight tracking-tight md:text-5xl">
              Providing clarity in international operations.
            </h2>
            <p className="mt-6 font-mono text-sm leading-relaxed text-muted-foreground">
              Growing businesses increasingly need to operate across borders — but understanding foreign
              markets, regulations, and business norms is difficult and risky when handled alone. Our primary
              mission is to be the trusted consulting partner that helps small to medium sized companies
              expand and compete internationally.
            </p>
            <p className="mt-4 font-mono text-sm leading-relaxed text-muted-foreground">
              Sourcing is the natural extension of that expertise. When you need to find, purchase, or ship
              components from outside your home country, we coordinate it through established third-party
              channels — giving you flexibility and reach without the overhead of building it yourself.
            </p>
          </Reveal>

          <Reveal className="media-frame relative overflow-hidden border border-white/20 md:col-span-6" delay={0.1}>
            <img src={IMG.cargo} alt="Cargo ship carrying international freight" className="h-full min-h-[340px] w-full object-cover" />
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="process-section" className="relative z-10 border-t border-white/10 bg-[hsl(155,38%,4%)]">
        <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary">[ How we work ]</p>
            <h2 className="mt-6 font-heading text-4xl font-bold uppercase leading-none tracking-tight md:text-6xl">
              A clear, four-step process
            </h2>
          </Reveal>

          <div className="mt-16 border-t border-white/10">
            {process.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.05}>
                <div className="group grid grid-cols-1 items-start gap-6 border-b border-white/10 py-10 transition-colors duration-500 hover:bg-white/[0.03] md:grid-cols-12">
                  <span className="font-heading text-5xl font-black text-white/15 transition-colors duration-500 group-hover:text-primary md:col-span-2 md:text-6xl">
                    {p.n}
                  </span>
                  <h3 className="font-heading text-2xl font-bold tracking-tight md:col-span-4 md:text-3xl">{p.title}</h3>
                  <p className="font-mono text-sm leading-relaxed text-muted-foreground md:col-span-6">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="relative z-10 mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary">[ What we stand for ]</p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="h-full border border-white/10 bg-white/[0.03] p-8 transition-colors duration-500 hover:border-primary">
                <h3 className="font-heading text-2xl font-bold tracking-tight text-primary">{v.title}</h3>
                <p className="mt-4 font-mono text-sm leading-relaxed text-muted-foreground">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-24" delay={0.1}>
          <div className="flex flex-col items-start justify-between gap-8 border border-white/10 bg-white/[0.03] p-10 md:flex-row md:items-center md:p-16">
            <div>
              <h2 className="font-heading text-3xl font-bold uppercase tracking-tight md:text-4xl">
                Ready to expand internationally?
              </h2>
              <p className="mt-3 font-mono text-sm text-muted-foreground">Tell us your goals — we&apos;ll help you navigate the path.</p>
            </div>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="group inline-flex shrink-0 items-center gap-3 bg-primary px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-primary-foreground transition-transform duration-300 hover:-translate-y-1"
            >
              Contact us
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
